import Alpine from "alpinejs"

const states = {
    default: "default",
    empty: "empty",
}

Alpine.data("vaccinesGrid", function () {
    return {
        filters: {}, // Stores selected items by filter ID
        activeFilter: null, // Tracks currently active filter ID
        states: states, // Tracks the state of the filter
        activeFilterTags: [], // Tracks currently active filter tags
        filtersList: [], // Stores references to each dropdown
        search: "", // Search term for filtering dropdown items
        state: states.default, // Current state of the grid
        visibleItems: 0, // Counter for visible items
        hasSearchTerm: false, // Flag to track if search is being used

        init() {
            // Get references to all dropdown elements
            this.filtersList = Array.from(this.$el.querySelectorAll(".c-dropdown"))

            // Listen for `dropdown:add-item` to handle item selection from child
            this.$el.addEventListener("dropdown:add-item", (event) => this.handleAddItem(event))

            // Listen for `dropdown:remove-item` to handle item deselection from child
            this.$el.addEventListener("dropdown:remove-item", (event) => this.handleRemoveItem(event))

            // Listen for `dropdown:clear-items` to handle clearing selection in child
            this.$el.addEventListener("dropdown:clear-items", (event) => this.handleClearItems(event))

            // Set up watcher for search input to check for empty state
            this.$watch("search", (value) => {
                this.hasSearchTerm = value.trim().length > 0
                this.checkVisibleItems()
            })

            // Initialize state check after DOM is fully loaded
            this.$nextTick(() => this.checkVisibleItems())
        },

        handleAddItem(event) {
            const { id, identifier, title } = event.detail

            // Add item to the selected filter
            if (!this.filters[id]) this.filters[id] = []
            if (!this.filters[id].some((item) => item.identifier === identifier)) {
                this.filters[id].push({ identifier, title })
                this.activeFilter = id // Set the active filter
                this.activeFilterTags = this.filters[id].map((item) => item.identifier)
                // Dispatch `dropdown:disable-filter` to each dropdown to control enabled/disabled state
                this.updateDropdownStates(this.activeFilter)

                // Check if we need to show empty state
                this.$nextTick(() => this.checkVisibleItems())
            }
        },

        handleRemoveItem(event) {
            const { id, identifier } = event.detail

            // Remove item from the selected filter
            if (this.filters[id]) {
                this.filters[id] = this.filters[id].filter((item) => item.identifier !== identifier)
                if (this.filters[id].length === 0) {
                    this.filters[id] = []
                    if (this.activeFilter === id) this.activeFilter = null
                }
                this.activeFilterTags = this.filters[id] ? this.filters[id].map((item) => item.identifier) : []
                // Dispatch `dropdown:disable-filter` to each dropdown to control enabled/disabled state
                this.updateDropdownStates(this.activeFilter)

                // Check if we need to show empty state
                this.$nextTick(() => this.checkVisibleItems())
            }
        },

        handleClearItems(event) {
            const { id } = event.detail

            // Clear selected items for the given filter ID
            if (this.filters[id]) this.filters[id] = []
            if (this.activeFilter === id) this.activeFilter = null
            this.activeFilterTags = []

            // Re-enable all filters by setting `enabledId` to null
            this.updateDropdownStates(null)

            // Reset state to default since we cleared filters
            this.$nextTick(() => this.checkVisibleItems())
        },

        updateDropdownStates(enabledId) {
            this.filtersList.forEach((filter) => {
                filter.dispatchEvent(
                    new CustomEvent("dropdown:disable-filter", {
                        bubbles: true,
                        detail: { enabledId },
                    }),
                )
            })
        },

        // New direct visibility check that processes all items at once
        // and updates the state based on visible count
        checkVisibleItems() {
            const items = this.$el.querySelectorAll(".c-vaccines-grid__items .c-vaccines-grid__item:not(.c-vaccines-grid__teaser)")
            let visibleCount = 0

            // Process each item and check visibility
            items.forEach((item) => {
                const searchTerm = this.search.toLowerCase()
                const title = item.dataset.title ? item.dataset.title.toLowerCase() : ""
                const byline = item.dataset.byline ? item.dataset.byline.toLowerCase() : ""
                // should be like this: {ageRanges: [], targetGroups: []}
                const filterTags = item.dataset.filterTags ? JSON.parse(item.dataset.filterTags) : []
                let activeByDropdown = false

                filterTags.forEach((tag) => {
                    // Check if the tag is in the active filter
                    if (this.filters[this.activeFilter]?.some((filter) => filter.identifier === tag)) {
                        activeByDropdown = true
                    }
                })

                // Default visibility
                let isItemVisible = true

                // If search term is present, check for matches
                if (this.search.trim()) {
                    isItemVisible = title.includes(searchTerm) || byline.includes(searchTerm)
                }

                // If active filter is set, check for matches
                if (this.activeFilter) {
                    isItemVisible = isItemVisible && activeByDropdown
                }

                if (isItemVisible) {
                    visibleCount++
                }
            })

            // Update visible items count and state
            this.visibleItems = visibleCount
            this.state = visibleCount > 0 ? states.default : states.empty

            // For debugging
            console.log(`Visible items: ${visibleCount}, State: ${this.state}`)
        },

        // This is still used by the x-show directive in the template
        isVisible(item) {
            const searchTerm = this.search.toLowerCase()
            const title = item.dataset.title ? item.dataset.title.toLowerCase() : ""
            const byline = item.dataset.byline ? item.dataset.byline.toLowerCase() : ""
            const filterTags = item.dataset.filterTags ? JSON.parse(item.dataset.filterTags) : []

            let activeByDropdown = false
            try {
                activeByDropdown = filterTags.some((tag) =>
                    this.filters[this.activeFilter]?.some((filter) => filter.identifier === tag),
                )
            } catch (e) {
                // ignore... probably no active filter or just still initializing
            }

            // If no search term or active filter, return true
            if (!this.search.trim() && !this.activeFilter) return true

            // Check if title or byline matches the search term
            let activeBySearch = title.includes(searchTerm) || byline.includes(searchTerm)

            // If active filter is set, check if item has the active filter tag
            if (!this.search.trim() && this.activeFilter) {
                return activeByDropdown
            } else if (!!this.search.trim() && !this.activeFilter) {
                return activeBySearch
            } else {
                return activeByDropdown && activeBySearch
            }
        },

        // Helper methods to determine current state
        isDefaultState() {
            return this.state === states.default
        },

        isEmptyState() {
            return this.state === states.empty
        },
    }
})
